/**
 * Attribution Reporting API integration (Phase 8.2).
 *
 * Chrome's ARA is the post-third-party-cookie way to measure conversions
 * without joining click events to conversion events server-side. Two
 * primitives:
 *
 *   - Source registration  — the browser remembers "user clicked an ad
 *     belonging to source X". We trigger this on click by either:
 *       (a) setting `attributionsrc` on the <a> the publisher's snippet
 *           injects (declarative path, works in stable Chrome), or
 *       (b) calling `window.attributionReporting?.registerSource(url)`
 *           (imperative path, only the experimental API).
 *
 *   - Trigger registration — fired at conversion time. We surface
 *     `registerAttributionTrigger(url)` for the merchant snippet to call.
 *
 * Both calls are best-effort: outside Chromium they're no-ops, and any
 * thrown error is swallowed because ARA is purely additive measurement.
 */

interface AraNavigator extends Navigator {
  attributionReporting?: {
    registerSource?: (url: string) => void;
    registerTrigger?: (url: string) => void;
  };
}

/**
 * Decorate an existing anchor with `attributionsrc` so a click both
 * navigates AND tells the browser to register an attribution source via
 * the URL the server provides. Safe to call repeatedly; idempotent.
 *
 * Returns true when we actually annotated the element.
 */
export function annotateClickAnchor(anchor: HTMLAnchorElement, sourceUrl?: string): boolean {
  if (!supportsAra()) return false;
  // The empty string means "use the anchor href itself", which is the
  // common case — the click URL the ad-server hands back is already the
  // attribution source endpoint.
  anchor.setAttribute('attributionsrc', sourceUrl ?? '');
  return true;
}

/**
 * Imperative source registration — useful when the publisher's snippet
 * fires its own click handler instead of letting the browser follow the
 * anchor's href.
 */
export function registerAttributionSource(sourceUrl: string): boolean {
  if (!supportsAra() || !sourceUrl) return false;
  const n = navigator as AraNavigator;
  try {
    if (typeof n.attributionReporting?.registerSource === 'function') {
      n.attributionReporting.registerSource(sourceUrl);
      return true;
    }
  } catch {
    // swallow — ARA is best-effort measurement, never blocks the user flow.
  }
  return false;
}

/**
 * Conversion-side trigger registration. Merchant snippet calls this when
 * the user converts; the browser joins it server-side via aggregation
 * services and emits the IAB-defined attribution reports.
 */
export function registerAttributionTrigger(triggerUrl: string): boolean {
  if (!supportsAra() || !triggerUrl) return false;
  const n = navigator as AraNavigator;
  try {
    if (typeof n.attributionReporting?.registerTrigger === 'function') {
      n.attributionReporting.registerTrigger(triggerUrl);
      return true;
    }
    // Fallback to a no-cors fetch with `attributionsrc` so the browser
    // picks the registration up from the response headers the ad-server
    // emits (Attribution-Reporting-Register-Trigger).
    if (typeof fetch === 'function') {
      void fetch(triggerUrl, {
        method: 'GET',
        mode: 'no-cors',
        credentials: 'omit',
        keepalive: true,
      });
      return true;
    }
  } catch {
    // swallow — measurement only.
  }
  return false;
}

function supportsAra(): boolean {
  if (typeof navigator === 'undefined') return false;
  const n = navigator as AraNavigator;
  // Chrome ships the API but gates it behind a Privacy Sandbox enrollment;
  // when disabled the object is undefined. We treat either an imperative
  // API binding OR the document-level attributionsrc support (>=Chrome
  // 117) as "ARA is available".
  if (n.attributionReporting !== undefined) return true;
  if (typeof HTMLAnchorElement !== 'undefined') {
    return 'attributionSrc' in HTMLAnchorElement.prototype;
  }
  return false;
}
